/** @format */

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import style from "./index.module.scss";
import cn from "classnames";
import InputMask from "react-input-mask";
import { countries, getCountryByCode, detectUserCountry } from "../../../utils/countryPhoneData";
import CloseIcon from "../../shared/icons/CloseIcon";
import { useTranslation } from "react-i18next";

const CustomPhoneInput = ({
  onChangeValue = () => void 0,
  onCountryChange = () => void 0,
  isDisabled = false,
  value = "",
  placeholder = "",
  labelText = "",
  errorText = "",
  defaultCountry = null,
  isDeleteText = false,
  isBgGray = false,
}) => {
  const { t } = useTranslation();

  const [selectedCountry, setSelectedCountry] = useState(() => {
    const initialCountry = defaultCountry || detectUserCountry();
    const country = getCountryByCode(initialCountry, t);
    return country;
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isInputFilled, setIsInputFilled] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;

    const updatePosition = () => {
      const button = buttonRef.current.getBoundingClientRect();
      const margin = 16;
      const gap = 4;
      const width = Math.min(280, window.innerWidth - margin * 2);
      const spaceBelow = window.innerHeight - button.bottom - gap - margin;
      const spaceAbove = button.top - gap - margin;
      const openAbove = spaceBelow < 300 && spaceAbove > spaceBelow;

      setDropdownPosition({
        left: Math.max(margin, Math.min(button.left, window.innerWidth - width - margin)),
        width,
        maxHeight: Math.max(0, Math.min(300, openAbove ? spaceAbove : spaceBelow)),
        ...(openAbove
          ? { bottom: window.innerHeight - button.top + gap }
          : { top: button.bottom + gap }),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    onCountryChange(selectedCountry.code);
  }, []);

  useEffect(() => {
    if (!value) {
      setPhoneNumber("");
      setIsInputFilled(false);
      return;
    }

    const phone = String(value).trim();
    const internationalPhone = phone.startsWith("+") ? phone : `+${phone}`;
    const matchedCountry = countries(t).find((country) => internationalPhone.startsWith(country.dialCode));

    if (matchedCountry) {
      setSelectedCountry((current) =>
        internationalPhone.startsWith(current.dialCode) ? current : matchedCountry,
      );
      setPhoneNumber(internationalPhone);
      setIsInputFilled(internationalPhone.replace(/\D/g, "").length > matchedCountry.dialCode.length - 1);
    }
  }, [value, t]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  const handlePhoneChange = (event) => {
    const newValue = event.target.value;
    setPhoneNumber(newValue);
    setIsInputFilled(
      newValue.replace(/[^0-9]/g, "").length > selectedCountry.dialCode.replace("+", "").length,
    );
    onChangeValue(newValue);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery("");

    setPhoneNumber(country.dialCode);
    setIsInputFilled(false);
    onChangeValue(country.dialCode);
    onCountryChange(country.code);
  };

  const toggleDropdown = () => {
    if (!isDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const clearInput = () => {
    setPhoneNumber(selectedCountry.dialCode);
    setIsInputFilled(false);
    onChangeValue(selectedCountry.dialCode);
  };

  const filteredCountries = countries(t).filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.dialCode.includes(searchQuery),
  );

  return (
    <div style={{ width: "100%" }}>
      <div className={style.wrapper}>
        {/* Селектор страны */}
        <div className={style.countrySelectWrapper}>
          <button
            ref={buttonRef}
            type="button"
            className={cn(style.countryButton, {
              [style.input_bg_wgray]: isBgGray,
              [style.disabled]: isDisabled,
              [style.error]: errorText,
            })}
            onClick={toggleDropdown}
            disabled={isDisabled}
          >
            <span className={style.flag}>{selectedCountry.flag}</span>
            <span className={style.dialCode}>{selectedCountry.dialCode}</span>
            <span className={cn(style.arrow, { [style.open]: isDropdownOpen })}>▼</span>
          </button>

          {/* Dropdown список стран */}
          {isDropdownOpen && (
            <div ref={dropdownRef} className={style.dropdown} style={dropdownPosition}>
              <div className={style.searchWrapper}>
                <input
                  type="text"
                  className={style.searchInput}
                  placeholder="Поиск страны..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <ul className={style.countryList}>
                {filteredCountries.map((country) => (
                  <li
                    key={country.code}
                    className={cn(style.countryItem, {
                      [style.selected]: country.code === selectedCountry.code,
                    })}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className={style.flag}>{country.flag}</span>
                    <span className={style.countryName}>{country.name}</span>
                    <span className={style.dialCode}>{country.dialCode}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Поле ввода телефона */}
        <div className={style.inputWrapper}>
          <label
            className={cn(style.labelInp, {
              [style.focusedLabel]: isFocused,
              [style.labelError]: errorText,
            })}
          >
            {labelText}
          </label>

          <InputMask
            mask={selectedCountry.mask}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={isDisabled}
            className={cn(style.input, {
              [style.input_disabled]: isDisabled,
              [style.input_bg_wgray]: isBgGray,
              [style.input_errorState]: errorText,
            })}
            placeholder={placeholder || selectedCountry.placeholder}
          >
            {(inputProps) => <input {...inputProps} type="text" />}
          </InputMask>

          {isDeleteText && isInputFilled && (
            <button type="button" className={style.prefixEnd} onClick={clearInput}>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {errorText && <div className={style.errorText}>{errorText}</div>}
    </div>
  );
};

export default CustomPhoneInput;
